import { useAuth0 } from '@auth0/auth0-react'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import LogoutIcon from '@mui/icons-material/Logout'
import { Avatar, Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Typography, useTheme } from '@mui/material'
import { FC, useCallback, useRef, useState } from 'react'
import { getUserInitials } from '../../utils/utils'
import { AppVersionDialog } from '../AppVersionDialog/AppVersionDialog'
import { Stack } from '@mui/system'

type UserProfileCardProps = {
  isExpanded: boolean
}

export const UserProfileCard: FC<UserProfileCardProps> = ({ isExpanded }) => {
  const { user, logout } = useAuth0()
  const theme = useTheme()
  // TODO: Re-enable this feature once the dark mode color palette is updated
  //const { mode, setMode } = useColorScheme()
  const userProfileMenuAnchorRef = useRef<HTMLDivElement>(null)
  const [userProfileMenuAnchor, setUserProfileMenuAnchor] = useState<null | HTMLElement>(null)
  const [isAboutDialogOpen, setIsAboutDialogOpen] = useState(false)

  const profileName = user?.name ?? ''
  const email = user?.email ?? ''
  const userInitials = getUserInitials(profileName)

  // TODO: Re-enable this feature once the dark mode color palette is updated
  /*const toggleColorModeButtonIcon = mode === 'dark' ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />
  const toggleColorModeButtonLabel = mode === 'dark' ? 'Turn Light Mode On' : 'Turn Dark Mode On'*/

  const isUserProfileMenuOpen = Boolean(userProfileMenuAnchor)

  const userAvatar = (
    <Avatar
      sx={{
        backgroundColor: 'var(--shape-medium)',
        color: 'var(--text-primary)',
        fontWeight: '700',

        // Responsive font size based on the number of initials.
        // the calc side will only apply when there are 4 or more initials
        fontSize: `min(var(--size-s), calc(var(--size-3xl) / ${userInitials.length} * 1.5))`
      }}
    >
      {userInitials}
    </Avatar>
  )

  const userProfileMenuButtonIcon = isExpanded ? isUserProfileMenuOpen ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon /> : userAvatar

  /**
   * Handles a click event on the open user profile menu button.
   */
  const handleOpenUserProfileMenuButtonClick = useCallback(() => {
    setUserProfileMenuAnchor(userProfileMenuAnchorRef.current)
  }, [])

  /**
   * Handles a close event on the user profile menu.
   */
  const handleUserProfileMenuClose = useCallback(() => {
    setUserProfileMenuAnchor(null)
  }, [])

  /**
   * Handles a click event on the toggle color mode button.
   *
   * Toggles the color mode preference between light and dark, based on the current color mode.
   */
  // TODO: Re-enable this feature once the dark mode color palette is updated
  /*const handleToggleColorModeButtonClick = useCallback(() => {
    if (mode === 'dark') {
      setMode('light')
    } else {
      setMode('dark')
    }
    handleUserProfileMenuClose()
  }, [mode, setMode, handleUserProfileMenuClose])*/


  const handleAboutButtonClick = useCallback(() => {
    handleUserProfileMenuClose()
    setIsAboutDialogOpen(true)
  }, [handleUserProfileMenuClose])


  const handleAboutDialogClose = useCallback(() => {
    setIsAboutDialogOpen(false)
  }, [])

  /**
   * Handles a click event on the logout button.
   *
   * Logs the user out of the application and closes the user profile menu.
   */
  const handleLogoutButtonClick = useCallback(() => {
    logout({
      logoutParams: {
        returnTo: `${window.location.origin}`
      }
    })
    handleUserProfileMenuClose()
  }, [logout, handleUserProfileMenuClose])

  return (
    <Box
      sx={{
        marginBlockStart: 'auto',
        padding: 'var(--spacing-2xs)'
      }}
    >
      {/* This box is required to act as an anchor point for the user profile menu. */}
      <Box ref={userProfileMenuAnchorRef}>
        <Menu
          anchorEl={userProfileMenuAnchor}
          open={isUserProfileMenuOpen}
          onClose={handleUserProfileMenuClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: isExpanded ? 'right' : 'left'
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: isExpanded ? 'right' : 'left'
          }}
          slotProps={{
            paper: {
              id: 'userProfileMenu',
              'aria-labelledby': 'open-user-profile-menu-button',
              sx: {
                // Upwards translation required to create a gap between the user profile card and its menu.
                // Also required to vertically align the menu when the user profile card is collapsed.
                translate: isExpanded ? '0 calc(-1 * var(--spacing-2xs))' : 'calc(-1 * var(--spacing-2xs)) calc(-1 * var(--spacing-2xs))',
                minInlineSize: '14rem'
              }
            }
          }}
        >
          {/* TODO: Re-enable this feature once the dark mode color palette is updated */}
          {/*<MenuItem onClick={handleToggleColorModeButtonClick} data-testid="toggle-color-mode-button">
            <ListItemIcon>{toggleColorModeButtonIcon}</ListItemIcon>
            <ListItemText>{toggleColorModeButtonLabel}</ListItemText>
          </MenuItem>*/}
          <MenuItem onClick={handleAboutButtonClick} data-testid="about-button">
            <ListItemIcon>
              <InfoOutlinedIcon />
            </ListItemIcon>
            <ListItemText>About</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleLogoutButtonClick} data-testid="logout-button">
            <ListItemIcon>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText>Sign out</ListItemText>
          </MenuItem>
        </Menu>

        <AppVersionDialog open={isAboutDialogOpen} onClose={handleAboutDialogClose} />

        <Stack
          direction="row"
          useFlexGap
          sx={{
            blockSize: isExpanded ? '5.5rem' : '4.5rem',
            minInlineSize: 0,
            columnGap: 'var(--spacing-s)',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingInline: isExpanded ? 'var(--spacing-m)' : 'var(--spacing-2xs)',
            paddingBlock: isExpanded ? 'var(--spacing-m)' : 'var(--spacing-2xs)',
            backgroundColor: 'var(--bg-surface-low)',
            borderRadius: 'var(--border-radius-s)',
            transition: theme.transitions.create('block-size', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen
            })
          }}
        >
          {isExpanded && (
            <Stack
              direction="row"
              useFlexGap
              sx={{
                minInlineSize: 0,
                columnGap: 'var(--spacing-s)'
              }}
            >
              {userAvatar}
              <Stack useFlexGap sx={{ minInlineSize: 0, rowGap: 'var(--spacing-3xs)' }}>
                <Typography
                  component="span"
                  title={profileName}
                  sx={{
                    color: 'var(--text-primary)',
                    fontSize: 'var(--size-xs)',
                    fontWeight: '500',
                    lineHeight: 'var(--line-height-xs)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {profileName}
                </Typography>
                <Typography
                  component="span"
                  title={email}
                  sx={{
                    color: 'var(--text-secondary)',
                    fontSize: 'var(--size-2xs)',
                    fontWeight: '400',
                    lineHeight: 'var(--line-height-2xs)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {email}
                </Typography>
              </Stack>
            </Stack>
          )}
          <IconButton
            onClick={handleOpenUserProfileMenuButtonClick}
            data-testid="open-user-profile-menu-button"
            title="Show user options"
            aria-controls={isUserProfileMenuOpen ? 'userProfileMenu' : undefined}
            aria-expanded={isUserProfileMenuOpen ? 'true' : undefined}
            aria-haspopup="true"
          >
            {userProfileMenuButtonIcon}
          </IconButton>
        </Stack>
      </Box>
    </Box>
  )
}
